import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismNumberInput,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import SettingsCostAdvancedMatrix from 'lumen/components/settings/cost/advanced-matrix';
import SettingsCostUnitToggle from 'lumen/components/settings/cost/unit-toggle';
import type {
  BookingWindowRow,
  CommissionMode,
  CommissionUnit,
} from 'lumen/utils/net-revenue/cost/types';
import {
  maxForUnit,
  placeholderForUnit,
  stepForUnit,
} from 'lumen/utils/net-revenue/cost/units';

interface SettingsCostCommissionCardSignature {
  Args: {
    bookingWindows: BookingWindowRow[];
    commissionValue: number | null;
    costError?: string;
    mode: CommissionMode;
    onAdvancedCellChange: (
      bookingWindowId: string,
      dayIndex: number,
      value: number | null,
    ) => void;
    onCommissionValueChange: (value: number | null) => void;
    onModeChange: (mode: CommissionMode) => void;
    onUnitChange: (unit: CommissionUnit) => void;
    unit: CommissionUnit;
  };
  Element: HTMLDivElement;
}

export default class SettingsCostCommissionCard extends Component<SettingsCostCommissionCardSignature> {
  get amountStep(): 0.01 | 1 {
    return stepForUnit(this.args.unit);
  }

  get maxAmount(): number | undefined {
    return maxForUnit(this.args.unit);
  }

  get placeholder(): string {
    return placeholderForUnit(this.args.unit);
  }

  @action handleModeChange(event: Event): void {
    this.args.onModeChange(
      (event.target as HTMLInputElement).value as CommissionMode,
    );
  }

  <template>
    <PrismCard
      @contentLayout="padded"
      data-test-id="cost-commission-card"
      ...attributes
    >
      <:header as |Header|>
        <Header.title>
          {{t "settings.cost.commission"}}
        </Header.title>
        <p class="text-neutral-subdued paragraph-200--regular">
          {{t "settings.cost.commission.description"}}
        </p>
      </:header>
      <:content>
        <div class="flex flex-col gap-400">
          <PrismRadioGroup
            @isHorizontal={{true}}
            @name="commission-mode"
            @onChange={{this.handleModeChange}}
            @value={{@mode}}
            as |RadioGroup|
          >
            <RadioGroup.input @value="simple" data-test-id="cost-mode-simple">
              {{t "settings.cost.mode.simple"}}
            </RadioGroup.input>
            <RadioGroup.input
              @value="advanced"
              data-test-id="cost-mode-advanced"
            >
              {{t "settings.cost.mode.advanced"}}
            </RadioGroup.input>
          </PrismRadioGroup>

          {{#if (eq @mode "advanced")}}
            <SettingsCostAdvancedMatrix
              @bookingWindows={{@bookingWindows}}
              @costError={{@costError}}
              @onCellChange={{@onAdvancedCellChange}}
              @onUnitChange={{@onUnitChange}}
              @unit={{@unit}}
            />
          {{else}}
            <div
              class="rounded-200 bg-neutral-subdued flex flex-col gap-100 p-400"
            >
              <span class="paragraph-200--bold">{{t
                  "settings.cost.amount.label"
                }}</span>
              <div class="flex items-start gap-200">
                <div class="w-[120px]">
                  {{#if (eq @unit "percentage")}}
                    <PrismNumberInput
                      @errorMessage={{@costError}}
                      @hasErrors={{if @costError true false}}
                      @isTouched={{if @costError true false}}
                      @max={{this.maxAmount}}
                      @min={{0}}
                      @onValueChange={{@onCommissionValueChange}}
                      @placeholder={{this.placeholder}}
                      @removePadding={{true}}
                      @step={{this.amountStep}}
                      @value={{@commissionValue}}
                      aria-label={{t "settings.cost.amount.label"}}
                      data-test-id="cost-commission-value"
                    />
                  {{else}}
                    <PrismNumberInput
                      @errorMessage={{@costError}}
                      @hasErrors={{if @costError true false}}
                      @isTouched={{if @costError true false}}
                      @max={{this.maxAmount}}
                      @min={{0}}
                      @onValueChange={{@onCommissionValueChange}}
                      @placeholder={{this.placeholder}}
                      @removePadding={{true}}
                      @step={{this.amountStep}}
                      @value={{@commissionValue}}
                      aria-label={{t "settings.cost.amount.label"}}
                      data-test-id="cost-commission-value"
                    />
                  {{/if}}
                </div>

                <SettingsCostUnitToggle
                  @onChange={{@onUnitChange}}
                  @unit={{@unit}}
                />

                <span
                  class="text-neutral-subdued paragraph-300--regular self-center"
                  data-test-id="cost-basis"
                >
                  {{t "settings.cost.basis.per_room_night"}}
                </span>
              </div>
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
